import React, { FC, useEffect } from 'react';

import { FormikValues, useFormik } from 'formik';
import { Box } from '@mui/system';
import { Typography } from '@mui/material';

import {
  AllowancesNames,
  convertToDecimals,
  pharmacistActions,
  pharmacistEvent,
  requestBooking,
  resetStatus,
  successSelector,
  userPreferredTimeFormat,
  currency,
  ICalendarEventType,
  AllowanceTypesToHandle,
  AllowanceEnums,
  TravelAllowanceEnums,
  getEventWithLocation,
} from '@pharmaplan/common';
import { handleWorkshiftToggles } from '@pharmaplan/common/helpers/utils';
import AllowanceWorkshift from '../Workshifts/AllowanceWorkshift';
import NotesField from '../../common/NotesField/NotesField';
import DateTimeRange from '../../common/DateTimeRange/DateTimeRange';
import { appendTimeToDate } from '../../../helpers/workshift/functions';
import { selectFooterPaginationCurrent } from '../../../selectors/drawerSelector';
import {
  renderScreen,
  resetDrawer,
  setBack,
  setButtons,
  setPagination,
} from '../../../actions/drawerActions';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

import { MomentTimeFormats, ScreenTypes } from '../../../helpers/Constants';
import { timeString } from '../../../helpers/Functions';
import strings from '../../../localization';

import {
  decimalConverterProxy,
  handlerForZeroes,
  parseFloatProxy,
} from '../../../helpers/proxyHandlers';
import { toJSDate } from '../../Dashboard/MainCalendar/helpers';
import { getDisableAvailability } from '../../../selectors/calendarSelector';
import { handleValues } from '../PharmacistSummary/helpers';
import useSyncWorkshiftValues from '../../../hooks/workshift/useSyncWorkshiftValues';

import classes from './styles';
import { getDayMonthDateYear } from '../../Reports/helpers';
import { AdminEditWorkshiftValidator } from '../../../helpers/commonValidators';

interface ISubmitCounterOffer {
  id: string;
  type: ICalendarEventType;
  eventIds: Array<string>;
  isReports?: boolean;
}

const { meal, travel, accommodation, others, emergency } = AllowanceEnums;

const {
  travelAllowanceType,
  accommodationAllowanceType: accommodationAllowanceTypeEnum,
  travelAllowanceRatePerKm: travelAllowanceRatePerKmEnum,
  travelAllowanceAmount: travelAllowanceAmountEnum,
  mealAllowance: mealAllowanceEnum,
  accommodationAllowance: accommodationAllowanceEnum,
  paidHourlyRate: paidHourlyRateEnum,
  emergencyFees: emergencyFeesEnum,
  otherAllowance: otherAllowanceEnum,
} = AllowancesNames;

const {
  align,
  timeContainer,
  title: titleStyle,
  counterValueText,
  container,
  divider,
  counterText,
  itemsContainer,
  labelContainer,
} = classes;

const { fullDate } = MomentTimeFormats;
const { paidAirTicket } = AllowanceTypesToHandle;

const SubmitCounterOffer: FC<ISubmitCounterOffer> = ({
  id,
  type,
  eventIds,
  isReports,
}) => {
  const dispatch = useAppDispatch();

  const timeFormat = useAppSelector(userPreferredTimeFormat);
  const current = useAppSelector(selectFooterPaginationCurrent);
  const event = useAppSelector(pharmacistEvent);
  const disabled = useAppSelector(getDisableAvailability);

  const success = useAppSelector((state) =>
    successSelector([pharmacistActions.requestBooking], state));

  const {
    startDate,
    endDate,
    hourlyRate,
    mealAllowance,
    travelAllowance,
    travelAllowanceTypes,
    emergencyFees,
    accommodationAllowance = 0,
    allTravelAllowanceTypes,
    accommodationAllowanceType,
    accommodationAllowanceTypes,
    travelAllowanceAmount,
    travelAllowanceRatePerKm,
    pharmacyName,
    bannerNumber,
    otherAllowance,
  } = event ?? {};

  const handleSubmit = (values: FormikValues) => {
    const { startTime, endTime, notes } = values ?? {};

    const val = {
      travelAllowanceRatePerKm: handleWorkshiftToggles(
        values[travelAllowanceType]?.includes(TravelAllowanceEnums.valueKm),
        values[travelAllowanceRatePerKmEnum],
        null,
      ),
      travelAllowanceAmount: handleWorkshiftToggles(
        values[travelAllowanceType].includes(TravelAllowanceEnums.travelAmount),
        values[travelAllowanceAmountEnum],
        null,
      ),
      mealAllowance: handleWorkshiftToggles(
        values[meal],
        values[mealAllowanceEnum],
        null,
      ),
      accommodationAllowance: handleWorkshiftToggles(
        values[accommodationAllowanceTypeEnum]
          === AllowanceTypesToHandle.paidValuePerNight,
        values[accommodationAllowanceEnum],
        null,
      ),
      emergencyFees: handleWorkshiftToggles(
        values[emergency],
        values[emergencyFeesEnum],
        null,
      ),
      otherAllowance: handleWorkshiftToggles(
        values[others],
        values[otherAllowanceEnum],
        null,
      ),
    };

    const handledForDecimals = new Proxy(val, parseFloatProxy);

    dispatch(
      requestBooking({
        workshiftid: id,
        counterOffer: {
          startDate: appendTimeToDate(startDate, startTime).toFormat(fullDate),
          endDate: appendTimeToDate(endDate, endTime).toFormat(fullDate),
          notes,
          hourlyRate: parseFloat(values[paidHourlyRateEnum]),
          travelAllowanceTypes: values[travelAllowanceType],
          accommodationAllowanceType: values[accommodationAllowanceTypeEnum],
          ...handledForDecimals,
        },
      }),
    );
  };

  const valuesToParse = {
    mealAllowance,
    emergencyFees,
    accommodationAllowance,
    paidHourlyRate: hourlyRate,
    travelAllowanceAmount,
    travelAllowanceRatePerKm,
    otherAllowance,
  };

  const valuesHandledForZero = new Proxy(valuesToParse, handlerForZeroes);
  const valuesHandledForDecimals = new Proxy(
    valuesHandledForZero,
    decimalConverterProxy,
  );

  const paidAirTicketGiven = travelAllowanceTypes?.includes(paidAirTicket)
    && allTravelAllowanceTypes?.find((item) =>
      item.key === paidAirTicket)?.value;

  const accommodationLabels = accommodationAllowanceTypes?.find(
    (item) =>
      item.key === accommodationAllowanceType,
  )?.value;

  const formik = useFormik({
    initialValues: {
      ...event,
      startTime: toJSDate(startDate),
      endTime: toJSDate(endDate),
      notes: '',
      travelAllowanceType: travelAllowanceTypes ?? [],
      accommodationAllowanceType: accommodationAllowanceType ?? null,
      ...valuesHandledForDecimals,
    },
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validationSchema: AdminEditWorkshiftValidator(),
  });

  const travelValue = handleValues([travelAllowance, paidAirTicketGiven]);

  const accommodationValue = handleValues([
    accommodationAllowance,
    accommodationLabels,
  ]);

  const config = [
    {
      key: 0,
      title: strings.time.toUpperCase(),
      value: `${timeString(startDate, endDate, timeFormat)}`,
      show: true,
    },
    {
      key: 1,
      title: strings.hourlyRate.toUpperCase(),
      value: `${currency}${convertToDecimals(hourlyRate)}`,
      show: true,
    },
    {
      key: 4,
      title: strings.meal.toUpperCase(),
      value: `$${convertToDecimals(mealAllowance)}`,
      show: mealAllowance,
    },
    {
      key: 2,
      title: strings.travel.toUpperCase(),
      value: travelValue,
      show: !!travelAllowanceTypes?.length,
    },
    {
      key: 3,
      title: strings.accomodation.toUpperCase(),
      value: accommodationValue,
      show: !!accommodationAllowanceType,
    },
    {
      key: 5,
      title: strings.other.toUpperCase(),
      value: `$${convertToDecimals(otherAllowance)}`,
      show: otherAllowance,
    },
  ].filter((item) =>
    item.show);

  const infoConfig = [
    {
      key: 1,
      title: strings.pharmacyName.toUpperCase(),
      value: pharmacyName,
      show: true,
    },
    {
      key: 2,
      title: strings.banner.toUpperCase(),
      value: bannerNumber,
      show: true,
    },
    {
      key: 3,
      title: strings.date.toUpperCase(),
      value: getDayMonthDateYear(startDate),
      show: true,
    },
  ];

  useEffect(() => {
    if (isReports) {
      dispatch(
        getEventWithLocation({
          id,
          type,
          latitude: null,
          longitude: null,
        }),
      );
    }
    dispatch(
      setBack(() => {
        dispatch(
          renderScreen({
            eventId: id,
            eventIds,
            type,
            screenNumber: 1,
            screenType: ScreenTypes.serviceWorkshiftDetails,
          }),
        );
      }),
    );

    // We don't need the back button for Workshift Details screen
    return () => {
      dispatch(setBack(null));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (success) {
      dispatch(resetDrawer());
      dispatch(resetStatus());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  useEffect(() => {
    dispatch(setPagination({ show: false, current }));

    dispatch(
      setButtons({
        show: true,
        primaryButton: {
          label: strings.submit,
          disabled: !disabled,
          onClick: formik.handleSubmit,
        },
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled]);

  useEffect(() => {
    formik.setFieldValue(meal, !!mealAllowance);
    formik.setFieldValue(travel, !!travelAllowanceTypes?.length);
    formik.setFieldValue(accommodation, !!accommodationAllowanceType);
    formik.setFieldValue(others, !!otherAllowance);
    formik.setFieldValue(emergency, !!emergencyFees);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event]);

  useSyncWorkshiftValues(formik);

  return (
    <>
      <Box sx={[container, align]}>
        <Typography sx={[counterValueText, titleStyle]}>
          {strings.offer}
        </Typography>
        <Box sx={divider} />
      </Box>

      <Box sx={itemsContainer}>
        {infoConfig.map((item) => {
          const { key, title, value } = item ?? {};

          return (
            <Box key={key} sx={[container, align, labelContainer]}>
              <Typography sx={[counterText]}>{title}</Typography>
              <Typography sx={[counterText, counterValueText]}>
                {value}
              </Typography>
            </Box>
          );
        })}
        {config.map((item) => {
          const { key, title, value } = item ?? {};

          return (
            <Box key={key} sx={[container, align, labelContainer]}>
              <Typography sx={[counterText]}>{title}</Typography>
              <Typography sx={[counterText, counterValueText]}>
                {value}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={[container, align]}>
        <Typography sx={[counterValueText, titleStyle]}>
          {strings.counterOffer}
        </Typography>
        <Box sx={divider} />
      </Box>

      <Box sx={timeContainer}>
        <DateTimeRange
          startPlaceholder="00:00"
          endPlaceholder="00:00"
          formik={formik}
          horizontal={false}
          startName="startTime"
          startNameLabel={strings.startTime}
          endName="endTime"
          endNameLabel={strings.endTime}
          usePreFilledColors
        />
      </Box>

      <AllowanceWorkshift formik={formik} isCounterOffer />

      <NotesField formik={formik} />
    </>
  );
};

export default SubmitCounterOffer;
